import { Info } from "@ondit/ui";
import { Link } from "react-router";
import { Async, Badge, KV, PageHead, Stat } from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Count, type Overview } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync } from "@/lib/hooks";

export function OverviewPage() {
	const t = useT();
	const { locale } = useI18n();
	const state = useAsync<Overview>(() => api.overview(), []);

	const count = (list: Count[], status: string) => list.find((c) => c.status === status)?.count ?? 0;

	return (
		<>
			<PageHead title={t("overview.title")} help={t("overview.lede")} />
			<Async state={state}>
				{(o) => {
					const capUsed =
						o.spend.platform_cap_micros > 0 ? o.spend.platform_today_micros / o.spend.platform_cap_micros : 0;
					const entities = o.entities.by_status.reduce((n, c) => n + c.count, 0);
					const pages = o.pages.reduce((n, c) => n + c.count, 0);
					return (
						<div className="stack">
							<p className="faint small">{t("overview.week", { week: o.week })}</p>
							<div className="stats">
								<Stat
									label={<Link to="/users">{t("overview.users")}</Link>}
									value={fmt.number(locale, o.users.total)}
									foot={`${t("overview.usersVerified", { n: o.users.verified })} · ${t("overview.usersNew", { n: o.users.new_7d })}`}
								/>
								<Stat
									label={<Link to="/orgs">{t("overview.orgs")}</Link>}
									value={fmt.number(locale, o.orgs.total)}
									foot={`${t("overview.orgsPaid", { n: o.orgs.paid })} · ${t("overview.orgsPaused", { n: o.orgs.paused })}`}
								/>
								<Stat
									label={<Link to="/entities">{t("overview.entities")}</Link>}
									value={fmt.number(locale, entities)}
									foot={t("overview.entitiesClaimed", { n: o.entities.claimed })}
								/>
								<Stat
									label={<Link to="/pages">{t("overview.pages")}</Link>}
									value={fmt.number(locale, count(o.pages, "published"))}
									foot={`${pages} · ${t("overview.pagesReview", { n: count(o.pages, "review") })}`}
									tone={count(o.pages, "review") > 0 ? "warn" : undefined}
								/>
								<Stat
									label={<Link to="/submissions">{t("overview.submissions")}</Link>}
									value={fmt.number(locale, o.submissions_pending)}
									tone={o.submissions_pending > 0 ? "warn" : undefined}
								/>
								<Stat
									label={<Link to="/jobs">{t("overview.jobs")}</Link>}
									value={fmt.number(locale, o.jobs.queued + o.jobs.running)}
									foot={`${t("overview.jobsQueued", { n: o.jobs.queued })} · ${t("overview.jobsRunning", { n: o.jobs.running })} · ${t("overview.jobsFailed", { n: o.jobs.failed })}`}
									tone={o.jobs.failed > 0 ? "danger" : undefined}
								/>
								<Stat
									label={t("overview.samples")}
									value={fmt.number(locale, o.samples_24h.total)}
									foot={`${t("overview.samplesNotices", { n: o.samples_24h.notices })} · ${fmt.usd(locale, o.samples_24h.cost_micros)}`}
									tone={o.samples_24h.notices > 0 ? "warn" : undefined}
								/>
								<Stat
									label={<Link to="/usage">{t("overview.spendToday")}</Link>}
									value={fmt.usd(locale, o.spend.platform_today_micros)}
									foot={t("overview.spendCap", { cap: fmt.usd(locale, o.spend.platform_cap_micros) })}
									tone={capUsed >= 0.9 ? "danger" : capUsed >= 0.6 ? "warn" : undefined}
								/>
								<Stat
									label={t("overview.spend30")}
									value={`${fmt.usd(locale, o.spend.cost_30d_micros)} / ${fmt.usd(locale, o.spend.billed_30d_micros)}`}
								/>
								<Stat
									label={t("overview.agents")}
									value={fmt.number(locale, o.agents.enabled)}
									foot={t("overview.agentsRunning", { n: o.agents.running })}
								/>
							</div>

							<details className="config-details">
								<summary>
									{t("overview.config")} <Info>{t("overview.configLede")}</Info>
								</summary>
								<div className="grid-2" style={{ marginTop: "var(--s-6)" }}>
									<div className="chips">
										{(
											[
												["paygate", o.config.paygate],
												["platform_key", o.config.platform_key],
												["mail", o.config.mail],
												["revalidate", o.config.revalidate],
												["google", o.config.google],
												["github", o.config.github],
											] as [string, boolean][]
										).map(([key, on]) => (
											<Badge key={key} tone={on ? "neutral" : "warning"}>
												{t(`config.${key}`)} · {t(on ? "config.on" : "config.off")}
											</Badge>
										))}
									</div>
									<KV
										items={[
											[
												t("config.origins"),
												`${o.config.public_origin} · ${o.config.app_origin} · ${o.config.api_origin}`,
											],
											["asale", o.config.gateway_url],
											[
												t("config.caps"),
												t("config.platformCap", { amount: fmt.usd(locale, o.spend.platform_cap_micros) }),
											],
											["", t("config.concurrent", { n: o.config.max_concurrent })],
										]}
									/>
								</div>
							</details>
						</div>
					);
				}}
			</Async>
		</>
	);
}
